import {
  centsToRatio,
  clamp01,
  createAdvancedVoice,
  createBodyResonatorBank,
  createModalBank,
  createNoiseBurst,
  hashSeed
} from "./dspPrimitives";
import { getVariantModelProfile } from "./instrumentModelProfiles";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, ModalSpec } from "./types";

function controlNumber(input: AdvancedSynthesisInput, id: string, fallback: number): number {
  const value = input.controls[id];

  return typeof value === "number" ? clamp01(value) : fallback;
}

export function renderMbira(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("mbira", input.variantId);
  const buzzAmount = input.ornament === "buzz" || input.ornament === "buzz_accent" ? 0.82 : controlNumber(input, "buzzAmount", 0.35);
  const gain = context.createGain();
  const tineBus = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, tineBus];
  const seedDetune = (hashSeed(input.noteLabel, input.instrumentId) % 17) - 8;
  const modes: ModalSpec[] = [
    { ratio: 1, gain: 0.18, decay: 0.76 + (variant?.decay ?? 0.8) * 0.34, detuneCents: seedDetune * 0.5 },
    { ratio: 5.02, gain: 0.065, decay: 0.48, detuneCents: seedDetune },
    { ratio: 14.1, gain: 0.036, decay: 0.24, detuneCents: -seedDetune * 0.8 },
    { ratio: 2.96, gain: 0.018, decay: 0.42, detuneCents: seedDetune * 0.25 },
    { ratio: 8.2, gain: 0.012, decay: 0.28 }
  ];
  const modal = createModalBank(context, input.frequency, modes, {
    destination: tineBus,
    velocity: input.velocity,
    now: input.now,
    family: "metal_tine_modal",
    quality: input.quality,
    totalGain: 0.9,
    duration: 2.2
  });
  const click = createNoiseBurst(context, {
    duration: 0.018,
    attack: 0.0005,
    decay: 3.4,
    gain: input.velocity * 0.08,
    filterType: "bandpass",
    frequency: 5200 + (variant?.brightness ?? 0.7) * 2400,
    q: 3.2,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "mbira-click"),
    tilt: "bright",
    startTime: input.now
  });

  gain.gain.value = input.velocity * 0.78;
  tineBus.connect(gain);
  click.output.connect(gain);
  click.source.start(input.now);
  sources.push(...modal.sources, click.source);
  cleanupNodes.push(...modal.cleanupNodes, ...click.cleanupNodes);
  cleanupNodes.push(
    ...createBodyResonatorBank(context, tineBus, gain, [
      { frequency: 185, q: 1.2, gainDb: 3.4, type: "peaking" },
      { frequency: 310, q: 1.0, gainDb: 2.6, type: "peaking" },
      { frequency: 1250, q: 1.8, gainDb: 2.2, type: "peaking" },
      { frequency: 2450, q: 1.6, gainDb: 1.8, type: "peaking" }
    ], variant?.bodyGain ?? 0.7)
  );

  if (buzzAmount > 0.03) {
    const buzz = createNoiseBurst(context, {
      duration: 0.42,
      attack: 0.016,
      decay: 1.6,
      gain: input.velocity * buzzAmount * 0.055,
      filterType: "bandpass",
      frequency: 1500 + buzzAmount * 1900,
      q: 1.3,
      seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "deze-buzz"),
      tilt: "bright",
      startTime: input.now + 0.018
    });

    buzz.output.connect(gain);
    buzz.source.start(input.now + 0.018);
    sources.push(buzz.source);
    cleanupNodes.push(...buzz.cleanupNodes);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.62,
    debug: {
      family: "metal_tine_modal",
      model: "mbira_cantilever_tine_deze",
      buzzAmount,
      layers: ["tine_1f_5f_14f", "hard_click", "calabash_body", buzzAmount > 0 ? "delayed_gated_buzz" : "buzz_closed"]
    }
  });
}

function bronzeModes(brightness: number, damped: boolean): ModalSpec[] {
  const decayScale = damped ? 0.24 : 1;
  const ratios = [1.0, 2.72, 5.18, 8.93, 13.4, 18.7, 25.1];

  return ratios.map((ratio, index) => ({
    ratio,
    gain: (index === 0 ? 0.2 : 0.12 / (index + 0.6)) * (index > 2 ? 0.62 + brightness * 0.8 : 1),
    decay: (0.42 / (1 + index * 0.16)) * decayScale,
    detuneCents: index % 2 === 0 ? -2 - index : 2 + index * 0.7,
    type: "sine" as const
  }));
}

export function renderGamelanSaron(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("gamelan_saron", input.variantId);
  const damped = input.ornament === "keyup_damping" || input.ornament === "damped" || input.ornament === "muted";
  const ombakDepth = input.ornament === "ombak" ? controlNumber(input, "ombakDepth", 0.75) : controlNumber(input, "ombakDepth", 0.28);
  const brightness = variant?.brightness ?? 0.68;
  const frequency = input.variantId === "saron_panerus" ? input.frequency * 1.42 : input.frequency;
  const gain = context.createGain();
  const barBus = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, barBus];
  const modal = createModalBank(context, frequency, bronzeModes(brightness, damped), {
    destination: barBus,
    velocity: input.velocity,
    now: input.now,
    family: "bronze_bar_modal",
    quality: input.quality,
    totalGain: damped ? 0.7 : 0.88,
    duration: damped ? 0.42 : 1.5
  });
  const malletMs = 0.001 + (1 - brightness) * 0.011;
  const mallet = createNoiseBurst(context, {
    duration: 0.03,
    attack: malletMs,
    decay: 2.8 + brightness * 1.3,
    gain: input.velocity * (0.04 + brightness * 0.055),
    filterType: "bandpass",
    frequency: 1800 + brightness * 4200,
    q: 2 + brightness * 3,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "mallet"),
    tilt: "bright",
    startTime: input.now
  });

  gain.gain.value = input.velocity * 0.78;
  barBus.connect(gain);
  mallet.output.connect(gain);
  mallet.source.start(input.now);
  sources.push(...modal.sources, mallet.source);
  cleanupNodes.push(...modal.cleanupNodes, ...mallet.cleanupNodes);

  if (ombakDepth > 0.18) {
    const beatTargetHz = 4 + ombakDepth * 6;
    const cents = 1200 * Math.log2((frequency + beatTargetHz * 0.5) / frequency);
    const lower = context.createOscillator();
    const upper = context.createOscillator();
    const pairGain = context.createGain();

    lower.type = "sine";
    upper.type = "sine";
    lower.frequency.setValueAtTime(frequency * centsToRatio(-cents), input.now);
    upper.frequency.setValueAtTime(frequency * centsToRatio(cents), input.now);
    pairGain.gain.setValueAtTime(input.velocity * ombakDepth * 0.055, input.now);
    pairGain.gain.setTargetAtTime(0.0001, input.now + (damped ? 0.14 : 0.5), damped ? 0.05 : 0.4);
    lower.connect(pairGain);
    upper.connect(pairGain);
    pairGain.connect(gain);
    lower.start(input.now);
    upper.start(input.now);
    lower.stop(input.now + (damped ? 0.5 : 1.8));
    upper.stop(input.now + (damped ? 0.5 : 1.8));
    sources.push(lower, upper);
    cleanupNodes.push(pairGain);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: damped ? 0.045 : 0.28,
    debug: {
      family: "bronze_bar_modal",
      model: "saron_inharmonic_bronze_bar",
      damped,
      ombakDepth,
      layers: ["inharmonic_partial_ratios", "mallet_hardness", damped ? "modal_decay_keyup_damped" : "open_bronze_decay", "controlled_ombak"]
    }
  });
}
